import {
  HALF_DATE_RANGE_DAYS,
  ISO_DATE_TIME_SEPARATOR,
  HOURS_IN_A_DAY,
  MS_IN_AN_HOUR
} from "@/features/risk-management/constants/generic-constants";
import i18n from "@/i18n";

/**
 * Generates default date range for risk management filters.
 * Creates a range that stays within the 120-day maximum limit.
 * Uses 60 days in the past and 60 days in the future for a total of 120 days.
 *
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export const getRiskManagementDateRange = () => {
  const today = new Date();

  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - HALF_DATE_RANGE_DAYS);

  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + HALF_DATE_RANGE_DAYS);

  const dateRange = {
    startDate: startDate.toISOString().split(ISO_DATE_TIME_SEPARATOR)[0],
    endDate: endDate.toISOString().split(ISO_DATE_TIME_SEPARATOR)[0]
  };

  return dateRange;
};

/**
 * Provides date constraints for date pickers in risk management filters.
 * Defines minimum and maximum selectable dates based on business rules.
 *
 *
 * @returns Object with min and max dates in YYYY-MM-DD format
 */
export const getRiskManagementDateConstraints = () => {
  const today = new Date();

  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - HALF_DATE_RANGE_DAYS);

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + HALF_DATE_RANGE_DAYS);

  return {
    min: minDate.toISOString().split(ISO_DATE_TIME_SEPARATOR)[0],
    max: maxDate.toISOString().split(ISO_DATE_TIME_SEPARATOR)[0]
  };
};

/**
 * Formats timestamps into user-friendly relative time strings.
 * Handles various input formats and provides localized output.
 *
 * @param lastUpdatedAt - Timestamp as string or Date object
 * @returns Formatted relative time string (e.g., "2 hours ago", "just now")
 */
export const formatLastUpdatedTime = (lastUpdatedAt: string | Date): string => {
  try {
    const lastUpdatedDate = new Date(lastUpdatedAt);
    if (isNaN(lastUpdatedDate.getTime())) {
      return i18n.global.t("RISK_MANAGEMENT.COMMONS.UNKNOWN");
    }

    const now = new Date();
    const diffInMs = now.getTime() - lastUpdatedDate.getTime();
    const diffInHours = Math.floor(diffInMs / MS_IN_AN_HOUR);

    // Format based on time difference
    if (diffInHours < HOURS_IN_A_DAY) {
      if (diffInHours <= 0) {
        return i18n.global.t("RISK_MANAGEMENT.COMMONS.JUST_NOW");
      } else if (diffInHours === 1) {
        return i18n.global.t("RISK_MANAGEMENT.COMMONS.ONE_HOUR_AGO");
      } else {
        return `${diffInHours} ${i18n.global.t("RISK_MANAGEMENT.COMMONS.HOURS_AGO")}`;
      }
    } else {
      const diffInDays = Math.floor(diffInHours / HOURS_IN_A_DAY);
      if (diffInDays === 1) {
        return i18n.global.t("RISK_MANAGEMENT.COMMONS.ONE_DAY_AGO");
      } else {
        return `${diffInDays} ${i18n.global.t("RISK_MANAGEMENT.COMMONS.DAYS_AGO")}`;
      }
    }
  } catch (error) {
    return i18n.global.t("RISK_MANAGEMENT.COMMONS.UNKNOWN");
  }
};
